import { ISurvey, Model, QuestionExpressionModel } from 'survey-core';

import { resolveDisplayValues } from './display-value-resolver';
import type { ExtractedExpression, ExtractedQuestion } from './types';

import './serializer';

const EXCLUDED_QUESTION_TYPES = ['expression', 'html', 'panel', 'paneldynamic'];

function extractFromCurlyBraces(str: string): string[] {
  const regex = /{([^}]*)}/g;
  const matches = str.match(regex);

  if (!matches) return [];

  return matches.map((match) => match.slice(1, -1).trim());
}

function buildNestedExpressions(survey: ISurvey, expr: string, visited: Set<string> = new Set()): Set<string> {
  const questionSet = new Set<string>();
  const questionNames = extractFromCurlyBraces(expr);

  for (const questionName of questionNames) {
    if (visited.has(questionName)) {
      continue;
    }
    visited.add(questionName);

    const iQuestion = survey.getQuestionByName(questionName);

    if (!iQuestion) {
      continue;
    }

    if (iQuestion.getType() === 'expression') {
      const nestedQuestions = buildNestedExpressions(
        survey,
        (iQuestion as QuestionExpressionModel).expression,
        visited
      );
      nestedQuestions.forEach((q) => questionSet.add(q));
    } else {
      questionSet.add(questionName);
    }
  }
  return questionSet;
}

function isExpressionSkipped(survey: ISurvey, expression: string): boolean {
  const questionNamesNeeded = Array.from(buildNestedExpressions(survey, expression));

  return !questionNamesNeeded.every((qName) => {
    return survey.getQuestionByName(qName).isVisible;
  });
}

function extractQuestionsAndExpressionsFromDefinition(
  definitionJson: string,
  answers: Record<string, unknown>
): { questions: ExtractedQuestion[]; resolvedAnswers: Record<string, unknown>; expressions: ExtractedExpression[] } {
  if (!answers) {
    return { questions: [], resolvedAnswers: {}, expressions: [] };
  }

  const model = new Model(JSON.parse(definitionJson));
  model.data = answers;

  const questions = model
    .getAllQuestions(true, false, true)
    .filter((q) => !EXCLUDED_QUESTION_TYPES.includes(q.getType().toLowerCase()))
    .map((q) => ({
      name: q.name,
      title: model.processText(extractTitle(q), true),
      type: q.getType(),
      inputType: q.getPropertyValue('inputType') || undefined,
    }));

  const resolvedAnswers = resolveDisplayValues(model, answers);

  const expressions = model
    .getAllQuestions(false, false, true)
    .filter((q) => q.getType().toLowerCase() === 'expression')
    .map((q) => ({
      conditionName: q.getPropertyValue('conditionName') || undefined,
      maxValue: q.getPropertyValue('maxValue') || undefined,
      measureName: q.getPropertyValue('measureName') || undefined,
      name: q.name,
      pageVisible: Boolean(q.page?.isVisible),
      skipped: !q.expression || isExpressionSkipped(model, q.expression),
      subscale: q.getPropertyValue('subscale') || undefined,
      thresholds: convertItemValues(q.getPropertyValue('thresholds')),
      title: extractTitle(q),
      value: typeof q.value === 'number' ? q.value : null,
    }));

  return { questions, resolvedAnswers, expressions };
}

function convertItemValues(itemValues: unknown): unknown[] {
  if (!Array.isArray(itemValues)) {
    return [];
  }
  return itemValues.map((item) => {
    if (typeof item === 'object' && item !== null && 'value' in item) {
      const result: Record<string, unknown> = { value: item.value };
      if ('text' in item) {
        result.text = item.text;
      }
      return result;
    }
    return item;
  });
}

function extractTitle(question: { name: string; title?: string | { default?: string } }): string {
  if (!question.title) {
    return question.name;
  }
  if (typeof question.title === 'string') {
    return question.title;
  }
  return question.title.default || question.name;
}

export {
  buildNestedExpressions,
  convertItemValues,
  EXCLUDED_QUESTION_TYPES,
  extractFromCurlyBraces,
  extractQuestionsAndExpressionsFromDefinition,
  extractTitle,
  isExpressionSkipped,
};
